import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Phone, Clock, AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";

/**
 * CallTrackerProvider — mounts a hidden, mandatory finish-call modal for
 * telecallers. Every phone call MUST go through `startCall(lead)` which:
 *   1. POSTs /staff/call-logs/start to record exact start_time
 *   2. Redirects the tab to tel:<number> (opens the phone dialer)
 *   3. Watches for tab visibility to come back ≥3s later
 *   4. Force-opens a modal that cannot be dismissed until outcome+remark
 *      submitted → POST /staff/call-logs/{id}/finish
 * The modal is a raw fixed overlay (not shadcn Dialog) so there is no
 * built-in close button or ESC handler that could bypass tracking.
 */

const Ctx = createContext(null);
export const useCallTracker = () => useContext(Ctx) || { startCall: () => {}, lastByLead: {}, todayStats: null, overdueCount: 0, refreshOverdue: () => {} };

const OUTCOMES = ["Interested", "Not Picked", "Busy", "Call Back", "Not Interested"];

function fmtSec(sec) {
  if (sec == null) return "";
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

export default function CallTrackerProvider({ children }) {
  const [active, setActive] = useState(null);        // {id, lead_id, lead_name, customer_number, start_time}
  const [showModal, setShowModal] = useState(false);
  const [hiddenAt, setHiddenAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [outcome, setOutcome] = useState("");
  const [remark, setRemark] = useState("");
  const [followup, setFollowup] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [lastByLead, setLastByLead] = useState({});   // {lead_id: last completed call}
  const [todayStats, setTodayStats] = useState(null);
  const [finishedTick, setFinishedTick] = useState(0); // bumps after every submitLog — dependents refetch
  const [overdueCount, setOverdueCount] = useState(0);

  // Fetch today's stats + pending call on mount and periodically
  const refreshToday = useCallback(async () => {
    try {
      const r = await api.get("/staff/call-logs/my-today");
      setTodayStats(r.data.stats);
      setLastByLead(r.data.last_by_lead || {});
    } catch { /* ignore */ }
  }, []);

  const refreshOverdue = useCallback(async () => {
    try {
      const r = await api.get("/staff/call-logs/overdue-followups");
      setOverdueCount((r.data.overdue || []).length);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    // Recover any dangling in-progress call from a previous session
    api.get("/staff/call-logs/pending").then(r => {
      const p = r.data.pending;
      if (p) {
        setActive(p);
        setShowModal(true);
        toast.warning("You have an unclosed call — please submit the log to continue.");
      }
    }).catch(() => {});
    refreshToday();
    refreshOverdue();
    const t = setInterval(() => { refreshToday(); refreshOverdue(); }, 30000);
    return () => clearInterval(t);
  }, [refreshToday, refreshOverdue]);

  // Live timer for the modal
  useEffect(() => {
    if (!showModal) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [showModal]);

  // Auto-open modal when user returns to the tab after ≥3s away with an active call
  useEffect(() => {
    const onVis = () => {
      if (!active) return;
      if (document.hidden) {
        if (!showModal) setHiddenAt(Date.now());
      } else {
        if (!showModal && hiddenAt) {
          const away = Date.now() - hiddenAt;
          if (away >= 3000) setShowModal(true);
          setHiddenAt(null);
        }
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [active, showModal, hiddenAt]);

  const startCall = useCallback(async (lead) => {
    if (active) {
      // Existing unfinished call — force the modal open, don't dial a second
      setShowModal(true);
      toast.error("Finish the previous call log first");
      return;
    }
    try {
      const r = await api.post("/staff/call-logs/start", { lead_id: lead.lead_id });
      setActive(r.data);
      setHiddenAt(Date.now());  // pretend they left now — desktop fallback
      setOutcome(""); setRemark(""); setFollowup("");
      // Delay dial slightly so state is flushed before navigation
      setTimeout(() => {
        window.location.href = `tel:${lead.phone || r.data.customer_number}`;
      }, 80);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [active]);

  const submitLog = async () => {
    if (!active) return;
    if (!outcome) { toast.error("Pick an outcome"); return; }
    if (outcome === "Interested" && !remark.trim()) { toast.error("Remark required for Interested"); return; }
    if (outcome === "Call Back" && !followup) { toast.error("Pick a follow-up date"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/staff/call-logs/${active.id}/finish`, {
        outcome,
        remark: remark.trim(),
        next_followup: followup || null,
      });
      if (r.data.is_fake) toast.warning(`Call flagged FAKE (<10s) — admin will see this`);
      else if (r.data.band === "short") toast.info(`Short call · ${fmtSec(r.data.duration_seconds)} — verify with customer`);
      else toast.success(`Call logged · ${fmtSec(r.data.duration_seconds)} · ${r.data.outcome}`);
      setActive(null);
      setShowModal(false);
      setHiddenAt(null);
      setOutcome(""); setRemark(""); setFollowup("");
      setFinishedTick(t => t + 1);
      refreshToday();
      refreshOverdue();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const secondsElapsed = active
    ? Math.max(0, Math.floor((nowTick - new Date(active.start_time).getTime()) / 1000))
    : 0;

  return (
    <Ctx.Provider value={{ startCall, active, lastByLead, todayStats, refreshToday, finishedTick, overdueCount, refreshOverdue }}>
      {children}

      {/* Floating pill — visible whenever there's an active call so user can end it manually */}
      {active && !showModal && (
        <button
          type="button"
          onClick={() => setShowModal(true)}
          data-testid="active-call-pill"
          className="fixed bottom-24 md:bottom-6 right-4 z-40 flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white text-sm font-bold rounded-full px-4 py-3 shadow-lg animate-pulse">
          <Phone className="w-4 h-4"/>
          <span>End Call · {fmtSec(secondsElapsed)}</span>
        </button>
      )}

      {/* Mandatory finish-call modal — raw overlay, no close button, no ESC dismiss */}
      {showModal && active && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="call-finish-dialog">
          <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl text-white shadow-2xl">
            <div className="p-5 border-b border-white/5">
              <div className="text-[10px] uppercase tracking-widest text-amber-500 font-bold">Call Log · Mandatory</div>
              <div className="mt-1 flex items-center gap-2 text-lg font-heading">
                <Phone className="w-5 h-5 text-emerald-400"/>
                Call to <span className="text-amber-400">{active.lead_name}</span>
              </div>
              <div className="text-xs text-slate-500 font-mono-alt mt-0.5">{active.customer_number}</div>
            </div>

            <div className="p-5 space-y-4">
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 flex items-center gap-3">
                <Clock className="w-5 h-5 text-emerald-400"/>
                <div className="text-3xl font-mono-alt text-emerald-300" data-testid="call-live-timer">
                  {fmtSec(secondsElapsed)}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 ml-auto">Live Duration</div>
              </div>

              {secondsElapsed < 10 && (
                <div className="flex items-center gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-4 h-4"/> Under 10 seconds — will be flagged FAKE
                </div>
              )}
              {secondsElapsed >= 10 && secondsElapsed <= 20 && (
                <div className="flex items-center gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-4 h-4"/> Short call ({secondsElapsed}s) — admin will verify
                </div>
              )}

              <div>
                <Label className="text-slate-300 text-xs">Outcome <span className="text-red-400">*</span></Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {OUTCOMES.map(o => (
                    <button key={o} type="button"
                      onClick={() => setOutcome(o)}
                      data-testid={`outcome-${o.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`px-3 py-2 text-xs rounded-lg border transition ${
                        outcome === o
                          ? "bg-amber-500 border-amber-500 text-slate-900 font-bold"
                          : "bg-slate-950 border-slate-700 text-slate-300 hover:border-amber-400/50"}`}>
                      {o}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-slate-300 text-xs">
                  Remark {outcome === "Interested" && <span className="text-red-400">*</span>}
                </Label>
                <Textarea data-testid="call-remark" value={remark}
                  onChange={(e) => setRemark(e.target.value)} rows={3}
                  placeholder={outcome === "Interested" ? "What exactly did the customer say?" : "Optional notes…"}
                  className="mt-1 bg-slate-950 border-slate-700 text-white text-sm"/>
              </div>

              {outcome === "Call Back" && (
                <div>
                  <Label className="text-slate-300 text-xs">Next follow-up <span className="text-red-400">*</span></Label>
                  <Input type="datetime-local" data-testid="call-followup" value={followup}
                    onChange={(e) => setFollowup(e.target.value)}
                    className="mt-1 bg-slate-950 border-slate-700 text-white"/>
                </div>
              )}

              <Button
                disabled={submitting || !outcome}
                onClick={submitLog}
                data-testid="submit-call-log"
                className="w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full disabled:opacity-40">
                {submitting ? "Submitting…" : "SUBMIT CALL LOG"}
              </Button>

              <p className="text-[10px] text-slate-500 text-center">
                This dialog cannot be closed until you submit. Every call is tracked for accountability.
              </p>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
